<template>
  <DModal :isBgOverlay="true" :show-close="false" @close="onCancel">
    <div class="do-modal do-modal__reasons-form" slot="insertComponent">
      <div class="do-modal__header">
        <h2>{{ i18n.create_absence }}</h2>
        <div class="icon__bg-circle ww-modal__header__close" @click="onCancel">
          <span class="icon-cross2"></span>
        </div>
      </div>
      <div class="do-modal__body">
        <div class="d-group marg-1-bottom">
          <label class="d-label">{{ i18n.title }}</label>
          <input
            class="d-input"
            v-focus
            v-model="newReasonTitle"
            @keyup.enter="onCreate"
            @input="clearErrors"
            :class="{ 'd-input--error': errors.newReasonTitle }"
          >
          <p class="input-error" v-if="errors.newReasonTitle">{{ errors.newReasonTitle }}</p>
        </div>
        <div class="d-label-and-toggle" @click.stop="defaultPrivate = !defaultPrivate">
          <label class="d-label">{{ i18n.hide_title }}</label>
          <AppToggle :propOn="defaultPrivate" :allowChanges="false"></AppToggle>
        </div>
        <p class="marg-1-top italic">
          <span v-html="i18n.default_private_explained"></span>
        </p>
      </div>
      <div class="do-modal__footer">
        <button class="do-bttn" @click="onCancel">{{ $t('vue2.common.cancel') }}</button>
        <button
          class="do-bttn do-bttn--submit"
          :class="{ 'do-bttn--submit--loading': isCreating }"
          @click="onCreate"
        >
          {{ $t('vue2.common.create') }}
        </button>
      </div>
    </div>
  </DModal>
</template>

<script>
const I18N = {
  da: {
    absence_reasons: 'Fraværsårsager',
    create_absence: 'Opret fraværsårsag',
    need_a_title: 'Mangler et navn',
    title: 'Titel på fraværsårsag',
    hide_title: 'Skjul fraværstitel',
    archive: 'Arkivér',
    dearchive: 'Genskab',
    no_absence_reasons_created: 'Ingen fraværsårsager oprettet',
    tooltip_for_hide_title: 'Når "Skjul titel" er aktiveret, er fraværstitlen kun synlig for administratorer og brugeren med fraværet – for andre vises blot "Fravær". Hvis indstillingen deaktiveres, er titlen synlig for alle, også i Aula.',
    title_already_exists: 'Navnet findes i forvejen',
    default_private_explained: 'Hvis "Skjul fraværstitel" er slået til, vil administratorer – samt brugeren med fraværet – se fraværets titel,<br>mens andre brugere blot ser "Fravær" som titel på fraværet.'
  },
  nb: {
    absence_reasons: 'Fraværsårsaker',
    create_absence: 'Opprett fraværsårsak',
    hide_title: 'Skjul tittel',
    archive: 'Arkiver',
    dearchive: 'Gjenopprett',
    title: 'Tittel',
    need_a_title: 'Mangler et navn',
    no_absence_reasons_created: 'Ingen fraværsårsaker opprettet',
    tooltip_for_hide_title: 'Når "Skjul tittel" er aktivert, er fraværstittelen kun synlig for administratorer og brukeren fraværet gjelder – for andre vises bare "Fravær". Hvis innstillingen deaktiveres, blir tittelen synlig for alle.',
    title_already_exists: 'Navnet finnes allerede',
    default_private_explained: 'Hvis "Skjul fraværstittel" er aktivert, vil administratorer – samt brukeren med fraværet – se fraværets tittel,<br>mens andre brukere kun ser "Fravær" som tittel på fraværet.'
  },
  en: {
    absence_reasons: 'Absence reasons',
    create_absence: 'Create absence reason',
    hide_title: 'Hide title',
    archive: 'Archive',
    dearchive: 'Restore',
    title: 'Title',
    need_a_title: 'Missing a name',
    no_absence_reasons_created: 'No absence reasons created',
    tooltip_for_hide_title: 'When "Hide title" is enabled, the absence title is only visible to administrators and the user the absence relates to – others will simply see "Absence". If the setting is disabled, the title becomes visible to everyone.',
    title_already_exists: 'The name already exists',
    default_private_explained: 'If "Hide absence title" is enabled, administrators – as well as the user with the absence – will see the absence title,<br>while other users will simply see "Absence" as the title.'
  }
}
import DModal from '../common/DModal.vue'
import AppToggle from '../common/AppToggle.vue'
export default {
  components: { DModal, AppToggle },
  props: {
    reasons: {
      type: Array,
      default: () => []
    },
    initTitle: {
      type: String,
      default: ''
    }
  },
  directives: {
    focus: {
      inserted (el) {
        setTimeout(function () {
          el.focus()
        }, 150)
      }
    }
  },
  data () {
    return {
      newReasonTitle: '',
      errors: {},
      isCreating: false,
      defaultPrivate: true
    }
  },
  computed: {
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N[this.locale]
    }
  },
  methods: {
    checkIfTitleAlreadyExists (title) {
      return this.reasons.some(
        reason => reason.name.toLowerCase() === this.newReasonTitle.toLowerCase()
      )
    },
    onCancel () {
      this.$emit('cancel')
      this.reset()
    },
    onCreate () {
      if (this.newReasonTitle.length < 1) {
        this.$set(this.errors, 'newReasonTitle', this.i18n.need_a_title)
        return
      }
      if (this.checkIfTitleAlreadyExists()) {
        this.$set(this.errors, 'newReasonTitle', this.i18n.title_already_exists)
        return
      }
      const titleAndDefaultPrivate = {
        title: this.newReasonTitle,
        defaultPrivate: this.defaultPrivate
      }
      this.isCreating = true
      this.$emit('create', titleAndDefaultPrivate)
    },
    clearErrors () {
      this.errors = {}
    },
    reset () {
      this.newReasonTitle = ''
      this.errors = {}
      this.isCreating = false
    }
  },
  mounted () {
    if (this.initTitle) {
      this.newReasonTitle = this.initTitle
    }
  }
}
</script>

<style scoped>
.do-modal__body {
  background: #edf0f5;
  max-height: calc(100vh - 105px);
  overflow-y: auto;
  overscroll-behavior: contain;
  min-width: 500px;
  padding: 2rem;
}

.do-modal__footer {
  gap: 1rem;
}


.d-label-and-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ccc;
  padding: 0.75rem;
  border-radius: 4px;
  cursor: pointer;
}

.d-label-and-toggle label {
  cursor: pointer;
}

.d-label-and-toggle:hover {
  background: aliceblue;
}

.d-group .d-input {
  height: 38px;
  margin-top: 0.25rem;
}


</style>



// WEBPACK FOOTER //
// AbsenceReasonsCreate.vue?5baf995a